import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('travel-futura-token'));

  // Check if user is authenticated on app start
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('travel-futura-token');
      const storedUser = localStorage.getItem('travel-futura-user');

      if (storedToken && storedUser) {
        try {
          // Verify token is still valid
          const response = await fetch('/api/admin-auth/verify', {
            headers: {
              'Authorization': `Bearer ${storedToken}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            setUser(data.data.user);
            setToken(storedToken);
          } else {
            // Token invalid or expired, clear stored data
            const errorData = await response.json().catch(() => ({}));
            console.log('Token verification failed:', errorData.message || 'Unknown error');
            localStorage.removeItem('travel-futura-token');
            localStorage.removeItem('travel-futura-user');
            setUser(null);
            setToken(null);
          }
        } catch (error) {
          console.error('Auth verification failed:', error);
          // Network error, clear stored data to be safe
          localStorage.removeItem('travel-futura-token');
          localStorage.removeItem('travel-futura-user');
          setUser(null);
          setToken(null);
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = (authData) => {
    setUser(authData.user);
    setToken(authData.token);
    localStorage.setItem('travel-futura-token', authData.token);
    localStorage.setItem('travel-futura-user', JSON.stringify(authData.user));
  };

  const logout = async () => {
    try {
      // Call logout API if token exists
      if (token) {
        await fetch('/api/admin-auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local data
      setUser(null);
      setToken(null);
      localStorage.removeItem('travel-futura-token');
      localStorage.removeItem('travel-futura-user');
    }
  };

  const isAuthenticated = () => {
    return !!(user && token);
  };

  const getAuthHeaders = () => {
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated,
    getAuthHeaders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
